import { HouseRule, ManualSection, Plate } from "./primitives"

const BATYR_DISPLAY_STACK = '"Batyr Display", "Aleo", serif'

// Three Lanes: the editorial type stack on the NYT model. Specimens of
// Batyr Display below use an explicit font stack because in product code
// the face belongs to the hero h1 alone — small task/tool titles opt into
// font-display (Aleo). The manual is the one place allowed to show the
// ladder outside that element.
export function TypeSection() {
  return (
    <ManualSection
      id="type"
      title="Three Lanes of Type"
      lede="A distinctive masthead voice, a slab serif for section headings, a humanist sans for everything else. Each font has one job; lanes never cross."
    >
      <div className="flex flex-col gap-6">
        <Plate
          title="Lane 1 · Batyr Display"
          note="The masthead voice — the one large hero heading that sets a page's emotional register (home hero, discovery indexes, content shows). Not every h1: small task/tool titles (forms, editors, admin) use font-display (Aleo) instead. Never inside cards, modals, or chrome. Sized with text-display-* utilities."
          importPath='h1 · "Batyr Display", 600'
        >
          <div className="flex flex-col gap-4">
            <p
              className="text-display-md text-primary"
              style={{ fontFamily: BATYR_DISPLAY_STACK, fontWeight: 600 }}
            >
              Songs that got me through
            </p>
            <p
              className="text-display-xs text-primary"
              style={{ fontFamily: BATYR_DISPLAY_STACK, fontWeight: 600 }}
            >
              Community playlist briefs
            </p>
          </div>
        </Plate>

        <div className="grid gap-6 lg:grid-cols-2">
          <Plate
            title="Lane 2 · Aleo"
            note="Slab serif for h2–h6, but only inside .app-canvas or .editorial scopes. Elsewhere headings stay on the body stack so kit components read correctly at small sizes."
            importPath='h2–h6 in editorial scopes · "Aleo", 600'
          >
            <div className="font-display flex flex-col gap-3 font-semibold">
              <p className="text-display-xs text-primary">
                The shared editorial desk
              </p>
              <p className="text-primary text-xl">
                Editorial frame, community texture
              </p>
              <p className="text-primary text-lg">
                Why this track made the cut
              </p>
            </div>
          </Plate>

          <Plate
            title="Lane 3 · Raleway"
            note="Body and every UI surface: buttons, labels, tables, forms. Hierarchy comes from the Ink opacity steps, not from size."
            importPath='body & UI · "Raleway", 400/600'
          >
            <div className="flex flex-col gap-4">
              <p className="text-secondary text-md max-w-prose">
                Every track carries a personal note explaining why it mattered
                to someone&rsquo;s mental health journey. The note is the
                product; the media embed is supporting material. Prose runs at a
                65–75 character measure.
              </p>
              <p className="text-primary text-sm font-semibold">
                Why does this fit? · Form label, Raleway 600
              </p>
            </div>
          </Plate>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <HouseRule name="The Three Lanes Rule">
            Batyr Display is the hero voice, not every h1 — the one large
            display heading that sets a page&rsquo;s emotional register. Small,
            task-oriented titles (forms, editors, admin, dashboards) stay on the
            UI heading font, Aleo (font-display), even as an h1. Aleo is
            headings-on-editorial-surfaces; Raleway is everything else. A second
            h1 for visual punch is still a violation.
          </HouseRule>
          <Plate title="A lane violation, marked" bodyClassName="gap-2">
            <p className="font-display text-secondary text-md max-w-prose">
              Aleo set as body copy reads like a pull quote that forgot to end;
              the slab serifs crowd at paragraph size.
            </p>
            <p className="text-sm">
              <span
                aria-hidden="true"
                className="text-batyr-red-pencil font-semibold"
              >
                ✑{" "}
              </span>
              <span className="text-primary decoration-batyr-red-pencil font-semibold underline decoration-2 underline-offset-4">
                stet not granted
              </span>
              <span className="text-tertiary">
                : headings only; body stays Raleway.
              </span>
            </p>
          </Plate>
        </div>
      </div>
    </ManualSection>
  )
}
